import React, { memo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop, tablet } from 'services/ui';

import { Colors, UserRole } from 'context';
import { ProgramLessonType } from 'components/types';
import { useReactiveVar } from '@apollo/client';
import { authState, useGroupTeacherContacts, useMyAttendance } from 'core';
import { GroupServiceInfo } from 'molecules/GroupServiceInfo';
import { TeacherSlackContacts } from 'molecules/TeacherSlackContacts';
import { GroupServiceInfo as IGroupService } from '../../../config/groupOverrides';

export interface ICourseInfo {
  courseName: string;
  groupName: string;
  studentsCount: number;
  passedLessonsCount: number;
  courseImageURL: string;
  lessons: ProgramLessonType[];
  lessonsCount: number;
  isFinished?: boolean | null;
  isFree?: boolean | null;
  /** Group whose attendance to show; omit to hide the attendance line. */
  groupId?: string;
  /**
   * Группа «без расписания»: прячем количество студентов, счётчик занятий и
   * собственную посещаемость. Считается в `CourseProgram` — только для студента.
   */
  hideSchedule?: boolean;
  /** Реквизиты услуги (BUR); показываются всем ролям. */
  service?: IGroupService | null;
}

export const CourseInfo = memo(
  ({
    courseImageURL,
    courseName,
    groupName,
    studentsCount,
    passedLessonsCount,
    lessonsCount,
    groupId,
    hideSchedule = false,
    service = null,
  }: ICourseInfo) => {
    const { t } = useTranslation();
    const { isDemoUser, active_role: activeRole } = useReactiveVar(authState);
    // The student's own attendance. The teacher has always recorded it, but
    // until now none of it reached the student — they could not answer
    // "how many lessons did I miss?". Self-scoped on the server.
    //
    // ⚠️ Роль-гейт обязателен ВМЕСТЕ с проброской groupId (ТЗ 0026): эта
    // карточка рендерится и преподавателю, а `myAttendance` — STUDENT-only и
    // вдобавок сверяет активную роль в токене. Без гейта починка мёртвого
    // кода немедленно уронила бы страницу курса у преподавателя на
    // PermissionDenied.
    const isStudent = activeRole === UserRole.STUDENT;
    const { attendance } = useMyAttendance(isStudent ? groupId || '' : '');
    // #58 «Napisz do prowadzącego» — только Slack. Роль-гейт внутри хука:
    // преподавателю `myGroupTeacherContacts` отвечает ошибкой прав. Пустой
    // список (нет Slack у группы / связки преподавателя) — блока нет.
    const { contacts: teacherContacts } = useGroupTeacherContacts(groupId);
    return (
      <Wrapper>
        <ImageWrapper>
          <CourseImage src={courseImageURL} alt="" />
        </ImageWrapper>
        <CourseInfoWrapper>
          <CourseName>{courseName}</CourseName>
          <InfoContainer>
            <Info>
              {t('courseInfo.group')} <InfoValue>{groupName}</InfoValue>
            </Info>
            {!hideSchedule && (
              <>
                <Info>
                  {t('courseInfo.amount')}
                  <InfoValue>
                    {isDemoUser ? 15 : studentsCount} {t('courseInfo.people')}
                  </InfoValue>
                </Info>
                <Info>
                  {t('courseInfo.lessons')}{' '}
                  <InfoValue>
                    {passedLessonsCount}/{lessonsCount}
                  </InfoValue>
                </Info>
                {/* Only once the teacher has marked something: the percentage is
                    over MARKED lessons, so before that there is nothing to say and
                    a "0%" would be a lie. */}
                {isStudent && attendance.percent !== null && (
                  <Info>
                    {t('courseInfo.myAttendance')}{' '}
                    <InfoValue>
                      {attendance.attended}/{attendance.marked} ({attendance.percent}%)
                    </InfoValue>
                  </Info>
                )}
              </>
            )}
          </InfoContainer>
          <ServiceInfoStyled service={service} />
          <TeacherContactsStyled contacts={teacherContacts} />
        </CourseInfoWrapper>
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  display: flex;
  margin-bottom: 24px;

  ${tablet} {
    flex-direction: row;
  }

  ${desktop} {
    flex-direction: column;
    margin: 0;
  }
`;

const ImageWrapper = styled.div`
  display: none;

  ${tablet} {
    margin-left: auto;
    display: block;
    overflow: hidden;
    width: 240px;
    height: 104px;
    border-bottom: 1px solid ${Colors.system_gray};
    order: 2;
  }

  ${desktop} {
    margin: 0;
    border-bottom: 1px solid ${Colors.bg_gray};
    order: 1;
  }
`;

const CourseImage = styled.img`
  width: 240px;
  height: 165px;
  object-fit: contain;
  object-position: center;
`;

const CourseInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  order: 1;

  ${desktop} {
    order: 2;
  }
`;

const CourseName = styled.h3`
  ${fontService.getFont({ size: 18, lineHeight: 133, weight: FontWeight.bold })}
  margin: 0 0 12px;
  max-width: 300px;

  ${tablet} {
    ${fontService.h3()}
    margin: 0 0 8px;
    max-width: 400px;
  }

  ${desktop} {
    margin: 24px 0 8px 0;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  margin-top: auto;
  flex-wrap: wrap;
  row-gap: 8px;

  ${tablet} {
    flex-wrap: nowrap;
    gap: 0;
  }

  ${desktop} {
    margin: 0;
  }
`;

const Info = styled.div`
  margin-right: 24px;
  ${fontService.text({ type: TextType.extraSmall })};

  &:last-child {
    margin: 0;
  }
`;

const InfoValue = styled.div`
  ${fontService.text({ type: TextType.normal, weight: FontWeight.bold })};
`;

const ServiceInfoStyled = styled(GroupServiceInfo)`
  margin-top: 16px;
`;

const TeacherContactsStyled = styled(TeacherSlackContacts)`
  margin-top: 16px;
`;
